// SPDX-License-Identifier: SEE LICENSE
//
// CertificateDoc — pixel-faithful port of the "Digital Device Certificate"
// claude.ai/design project (831d128c…), with every placeholder wired to the
// real minted-certificate data and a real, scannable QR code.
//
// The design is authored at a fixed 1586×992 canvas; CertificateDoc renders it
// at that exact size inside a ResizeObserver scale-to-fit wrapper so it always
// fills the available width without distortion.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';


const DW = 1586;
const DH = 992;

// The canonical public origin for the verifier. The QR must ALWAYS encode this
// domain so a scan opens the gilded web cert view — even when the dashboard is
// being viewed via the raw IP (http://79.72.79.141), where window.location.origin
// would otherwise bake the IP into the QR.
const CANONICAL_ORIGIN = 'https://verify.retronixs.com';

// ── data shape (mirrors the cert bundle) ─────────────────────────────────────
export interface CertPillar {
  pillar: string;
  title: string;
  verdict: string;
  merkleLeaf: string;
}
export interface CertDocData {
  short?: string;
  verify_token?: string;
  session_id?: string;
  merkle_root_hex?: string;
  overall?: string;
  key_attested?: boolean;
  created_at?: string;
  pdf_url?: string;
  bundle_url?: string;
  bundle?: {
    credentialSubject?: {
      device?: { manufacturer?: string; model?: string };
      sessionMerkleRoot?: string;
      sessionMerkleRootSource?: string;
      overallVerdict?: string;
      verdictCounts?: Record<string, number>;
      pillars?: CertPillar[];
      deviceAttestation?: {
        keyAttested?: boolean;
        keyFingerprintSha256?: string;
        keyAlias?: string;
        securityLevel?: string;
        deviceLocked?: boolean;
        verifiedBootState?: string;
        attestationRoot?: string;
      };
      anchors?: {
        rekorUrl?: string;
        rekorLogIndex?: string;
        openTimestampsCalendars?: string[];
        openTimestampsStatus?: string;
      };
    };
    proof?: { proofValue?: string; verificationMethod?: string };
  };
}

// ── guilloché rosette (hypotrochoid) — ported from the design's DCLogic ──────
function hypo(cx: number, cy: number, R: number, r: number, d: number, turns: number, steps: number): string {
  let s = '';
  const k = (R - r) / r;
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * Math.PI * 2 * turns;
    const x = cx + (R - r) * Math.cos(t) + d * Math.cos(k * t);
    const y = cy + (R - r) * Math.sin(t) - d * Math.sin(k * t);
    s += (i ? 'L' : 'M') + x.toFixed(2) + ',' + y.toFixed(2);
  }
  return s;
}
function Rosette({ stroke, sw, dens = 90 }: { stroke: string; sw: number; dens?: number }) {
  const specs: [number, number, number][] = [[97, 12, 86], [89, 11, 72], [70, 9, 60], [48, 7, 42]];
  return (
    <svg viewBox="0 0 200 200" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
      {[96, 74, 52, 32, 16].map((r, i) => (
        <circle key={'c' + i} cx={100} cy={100} r={r} fill="none" stroke={stroke} strokeWidth={sw * 0.6} />
      ))}
      {specs.map((p, i) => (
        <path key={'p' + i} d={hypo(100, 100, p[0], p[1], p[2], p[1], p[1] * dens)} fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" />
      ))}
    </svg>
  );
}

// ── corner filigree (appears on every panel) ─────────────────────────────────
function CornerOrn() {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none">
      <path d="M2 9 V4 Q2 2 4 2 H9" stroke="rgba(201,162,90,0.6)" strokeWidth={1.1} fill="none" />
      <path d="M5 12 Q5 5 12 5" stroke="rgba(201,162,90,0.35)" strokeWidth={0.8} fill="none" />
      <circle cx={5.5} cy={5.5} r={1.5} fill="rgba(214,180,110,0.85)" />
    </svg>
  );
}
function Corners() {
  const mk = (st: CSSProperties, rot: number) => (
    <span key={rot} style={{ position: 'absolute', width: 24, height: 24, transform: `rotate(${rot}deg)`, pointerEvents: 'none', zIndex: 2, ...st }}>
      <CornerOrn />
    </span>
  );
  return (
    <>
      {mk({ left: 7, top: 7 }, 0)}
      {mk({ right: 7, top: 7 }, 90)}
      {mk({ right: 7, bottom: 7 }, 180)}
      {mk({ left: 7, bottom: 7 }, 270)}
    </>
  );
}

function SpineCol() {
  const stroke = 'rgba(245,236,208,0.20)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {Array.from({ length: 7 }, (_, i) => (
        <div key={i} style={{ width: 180, height: 180, marginTop: i ? -40 : -14, flex: 'none' }}>
          <Rosette stroke={stroke} sw={0.5} dens={70} />
        </div>
      ))}
    </div>
  );
}

// ── verdict pill ─────────────────────────────────────────────────────────────
const PILL: Record<string, { border: string; bg: string; glow: string; color: string; label: string }> = {
  passed: { border: 'rgba(70,150,95,0.7)', bg: 'rgba(28,64,44,0.45)', glow: 'rgba(60,160,100,0.18)', color: '#5cc483', label: 'PASSED' },
  concern: { border: 'rgba(168,116,40,0.85)', bg: 'rgba(74,52,18,0.5)', glow: 'rgba(190,130,40,0.2)', color: '#e0992f', label: 'CONCERN' },
  failed: { border: 'rgba(170,58,64,0.85)', bg: 'rgba(74,26,30,0.5)', glow: 'rgba(200,60,68,0.22)', color: '#e2515b', label: 'FAILED' },
  skipped: { border: 'rgba(86,98,124,0.75)', bg: 'rgba(40,50,70,0.42)', glow: 'rgba(100,116,150,0.15)', color: '#8493ab', label: 'SKIPPED' },
};
function VerdictIcon({ kind, color }: { kind: string; color: string }) {
  if (kind === 'concern')
    return (
      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3 22 20H2Z" /><path d="M12 10v4" /><path d="M12 17h.01" />
      </svg>
    );
  if (kind === 'failed')
    return (
      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round">
        <circle cx={12} cy={12} r={9} /><path d="m9 9 6 6M15 9l-6 6" />
      </svg>
    );
  if (kind === 'skipped')
    return (
      <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2}>
        <circle cx={12} cy={12} r={9} /><path d="M8 12h8" />
      </svg>
    );
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2}>
      <circle cx={12} cy={12} r={9} /><path d="m8.5 12 2.4 2.4 4.6-5" />
    </svg>
  );
}
function VerdictPill({ verdict }: { verdict: string }) {
  const v = PILL[verdict] ?? PILL.skipped!;
  return (
    <span style={{ justifySelf: 'end', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 11px', borderRadius: 7, border: `1px solid ${v.border}`, background: v.bg, boxShadow: `0 0 9px ${v.glow}`, font: "700 12.5px/1 'Inter'", letterSpacing: 0.8, color: v.color }}>
      <VerdictIcon kind={verdict} color={v.color} />
      {v.label}
    </span>
  );
}

// ── grade banner ─────────────────────────────────────────────────────────────
const GRADE: Record<string, { grade: string; label: string; stroke: string; inner: string; fill: string; text: string; glow: string }> = {
  pass: { grade: 'A', label: 'ALL CHECKS PASSED', stroke: '#3c8a5e', inner: 'rgba(60,160,100,0.4)', fill: 'rgba(20,52,34,0.55)', text: '#4fd089', glow: 'rgba(60,160,100,0.45)' },
  concern: { grade: 'B', label: 'MINOR CONCERNS', stroke: '#a8762a', inner: 'rgba(214,150,52,0.4)', fill: 'rgba(54,38,14,0.55)', text: '#e0992f', glow: 'rgba(214,150,52,0.45)' },
  fail: { grade: 'C', label: 'FAILED CHECKS', stroke: '#a4303a', inner: 'rgba(214,52,64,0.4)', fill: 'rgba(54,14,20,0.55)', text: '#e0414b', glow: 'rgba(214,52,64,0.45)' },
  incomplete: { grade: '—', label: 'INCOMPLETE', stroke: '#5a6678', inner: 'rgba(120,140,170,0.4)', fill: 'rgba(28,36,52,0.55)', text: '#9aa8bd', glow: 'rgba(120,140,170,0.35)' },
};

function dash(s: string | undefined | null): string {
  return s != null && s !== '' ? s : '—';
}

// ============================================================================
// The fixed-canvas certificate (1586×992).
// ============================================================================
function Certificate1586({ cert, sessionId, dh }: { cert: CertDocData; sessionId?: string | undefined; dh: number }) {
  const s = cert.bundle?.credentialSubject;
  const dev = s?.device;
  const deviceLabel = dev ? `${dev.manufacturer ?? ''} ${dev.model ?? ''}`.trim() : '—';
  const session = dash(sessionId ?? cert.session_id);
  const merkleRoot = dash(cert.merkle_root_hex ?? s?.sessionMerkleRoot);
  const rootSource = dash(s?.sessionMerkleRootSource);
  const attKey = dash(s?.deviceAttestation?.keyFingerprintSha256 ?? s?.deviceAttestation?.keyAlias);
  const secLevel = s?.deviceAttestation?.securityLevel;
  const attLevel = cert.key_attested
    ? `TEE hardware-attested${secLevel ? ` · ${secLevel}` : ''}`
    : 'software-backed key';
  const overall = (cert.overall ?? s?.overallVerdict ?? 'incomplete').toLowerCase();
  const g = GRADE[overall] ?? GRADE.incomplete!;

  const anchors = s?.anchors;
  const rekorIndex = anchors?.rekorLogIndex;
  const otsCals = anchors?.openTimestampsCalendars?.length ?? 0;
  const otsStatus = anchors?.openTimestampsStatus;
  const proofValue = cert.bundle?.proof?.proofValue;
  const issuerSig = proofValue && proofValue.length > 0 ? proofValue : '—';
  // Issuer DID from the signed proof (strip the key fragment) — the resolvable
  // did:web we publish at /.well-known/did.json, not a dead domain.
  const issuerDid = (cert.bundle?.proof?.verificationMethod?.split('#')[0] || 'did:web:verify.retronixs.com').toUpperCase();

  const created = cert.created_at ? new Date(cert.created_at) : null;
  const ymd = created
    ? `${created.getUTCFullYear()}${String(created.getUTCMonth() + 1).padStart(2, '0')}${String(created.getUTCDate()).padStart(2, '0')}`
    : '00000000';
  const certId = cert.short ? `RTN-DDC-${ymd}-${cert.short.toUpperCase()}` : '—';

  // Real, scannable QR → the SAME certificate, rendered as a clean public web
  // view. Pinned to CANONICAL_ORIGIN (a phone camera can't open a host-less
  // path, and never the raw IP). The QR opens the PUBLIC single-cert verify
  // page `/verify/:short` — keyed by the cert's unguessable short-id, it shows
  // only THIS certificate and requires no login, while the operator dashboard,
  // session list, and all other certs stay locked behind auth.
  const verifyUrl =
    cert.short && cert.verify_token
      ? `${CANONICAL_ORIGIN}/verify/${cert.short}?t=${cert.verify_token}`
      : CANONICAL_ORIGIN;

  const pillars = s?.pillars ?? [];

  const panelBg = 'linear-gradient(180deg,rgba(15,26,48,0.66),rgba(9,16,32,0.66))';
  const panelBorder = '1px solid rgba(176,146,82,0.3)';
  const panelShadow = 'inset 0 0 0 1px rgba(176,146,82,0.05),inset 0 0 40px rgba(0,0,0,0.35)';
  const sectionTitle: CSSProperties = { font: "600 18px/1 'Cinzel',serif", letterSpacing: 2.5, color: '#d8b56a', whiteSpace: 'nowrap', flex: 'none' };
  const rowLabel: CSSProperties = { font: "600 12px/1 'Inter'", letterSpacing: 1.5, color: '#9c855a' };

  const attRow = (label: string, value: ReactNode, mono = false, last = false): ReactNode => (
    <div style={{ display: 'grid', gridTemplateColumns: '152px 1fr', alignItems: 'center', minHeight: 34, padding: '7px 0', borderBottom: last ? 'none' : '1px solid rgba(176,146,82,0.1)' }}>
      <span style={rowLabel}>{label}</span>
      <span style={mono ? { font: "400 13.5px/1.45 'JetBrains Mono'", color: '#a6b6d2', letterSpacing: 0.2, wordBreak: 'break-all' } : { font: "700 16px/1.3 'Inter'", color: '#eef2f8' }}>{value}</span>
    </div>
  );

  return (
    <div
      style={{
        position: 'relative',
        width: DW,
        height: dh,
        fontFamily: "'Inter',sans-serif",
        backgroundColor: '#070d1c',
        backgroundImage:
          'radial-gradient(circle at 1px 1px, rgba(201,162,90,0.05) 0.5px, transparent 1.2px), radial-gradient(125% 95% at 50% -5%, #0e1c38 0%, #091226 46%, #050a16 100%)',
        backgroundSize: '7px 7px, 100% 100%',
        overflow: 'hidden',
      }}
    >
      {/* ORNAMENTAL FRAME */}
      <div style={{ position: 'absolute', inset: 11, border: '1.5px solid rgba(193,162,96,0.55)', borderRadius: 7, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 19, border: '1px solid rgba(150,120,66,0.4)', borderRadius: 4, pointerEvents: 'none' }} />
      {([['left', 'top'], ['right', 'top'], ['left', 'bottom'], ['right', 'bottom']] as const).map(([x, y]) => (
        <div key={x + y} style={{ position: 'absolute', [x]: 7, [y]: 7, width: 9, height: 9, background: '#c9a25a', transform: 'rotate(45deg)', pointerEvents: 'none' } as CSSProperties} />
      ))}

      {/* GUILLOCHE ENGRAVING LAYER */}
      <div style={{ position: 'absolute', inset: 22, overflow: 'hidden', borderRadius: 4, pointerEvents: 'none', zIndex: 0 }}>
        <div style={{ position: 'absolute', left: '50%', top: '50%', width: 820, height: 820, transform: 'translate(-50%,-50%)', opacity: 0.055 }}>
          <Rosette stroke="rgba(201,162,90,0.95)" sw={0.45} dens={90} />
        </div>
        {([['left', 'top'], ['right', 'top'], ['left', 'bottom'], ['right', 'bottom']] as const).map(([x, y]) => (
          <div key={x + y} style={{ position: 'absolute', [x]: -160, [y]: -160, width: 420, height: 420, opacity: 0.18 } as CSSProperties}>
            <Rosette stroke="rgba(201,162,90,0.95)" sw={0.5} dens={90} />
          </div>
        ))}
      </div>

      {/* TOP-LEFT SHIELD EMBLEM */}
      <div style={{ position: 'absolute', left: 44, top: 34, width: 62, height: 76, zIndex: 3 }}>
        <svg width={62} height={76} viewBox="0 0 62 76" fill="none">
          <defs>
            <linearGradient id="emGold" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#f6e6b4" /><stop offset="0.5" stopColor="#d4a957" /><stop offset="1" stopColor="#9c7330" />
            </linearGradient>
          </defs>
          <path d="M31 3 L57 12 V41 C57 59 45 68 31 73 C17 68 5 59 5 41 V12 Z" fill="#0c1730" stroke="url(#emGold)" strokeWidth={2.4} />
          <path d="M31 7 L53 15 V41 C53 56 43 64 31 69 C19 64 9 56 9 41 V15 Z" fill="none" stroke="rgba(212,169,87,0.35)" strokeWidth={0.8} />
          <text x={31} y={50} textAnchor="middle" fontFamily="Cinzel" fontWeight={700} fontSize={30} fill="url(#emGold)">R</text>
        </svg>
      </div>

      {/* MAIN CONTENT */}
      <div style={{ position: 'absolute', left: 40, top: 22, right: 196, bottom: 20, display: 'flex', flexDirection: 'column', zIndex: 1 }}>
        {/* HEADER */}
        <div style={{ flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 30 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <span style={{ width: 120, height: 1, background: 'linear-gradient(90deg,transparent,rgba(201,162,90,0.75))' }} />
              <span style={{ width: 7, height: 7, background: '#c9a25a', transform: 'rotate(45deg)' }} />
            </span>
            <h1 style={{ margin: 0, fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: 58, lineHeight: 1, letterSpacing: 13, background: 'linear-gradient(180deg,#f8e9bb 0%,#e8c97f 40%,#cb9b4d 72%,#9c7330 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent', textShadow: '0 2px 4px rgba(0,0,0,0.4)', paddingLeft: 13, whiteSpace: 'nowrap' }}>RETRONICS</h1>
            <span style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <span style={{ width: 7, height: 7, background: '#c9a25a', transform: 'rotate(45deg)' }} />
              <span style={{ width: 120, height: 1, background: 'linear-gradient(90deg,rgba(201,162,90,0.75),transparent)' }} />
            </span>
          </div>
          <h2 style={{ margin: '8px 0 0', fontFamily: "'Cinzel',serif", fontWeight: 600, fontSize: 40, lineHeight: 1, letterSpacing: 7, background: 'linear-gradient(180deg,#ffffff 0%,#dfe6ef 46%,#9aa6b6 100%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent', paddingLeft: 7, whiteSpace: 'nowrap' }}>DIGITAL DEVICE CERTIFICATE</h2>
          <div style={{ marginTop: 16, font: "600 13.5px/1 'Inter'", letterSpacing: 2.2, color: '#b59a60', whiteSpace: 'nowrap' }}>
            COMPOSITE 13-PILLAR ATTESTATION<span style={{ color: '#6c5a30', margin: '0 13px' }}>&#8226;</span>SESSION MERKLE ROOT<span style={{ color: '#6c5a30', margin: '0 13px' }}>&#8226;</span>{cert.key_attested ? 'HARDWARE-BOUND' : 'SOFTWARE-SIGNED'}<span style={{ color: '#6c5a30', margin: '0 13px' }}>&#8226;</span>PUBLICLY VERIFIABLE
          </div>
          {/* grade banner */}
          <div style={{ position: 'relative', marginTop: 16, width: 480, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={480} height={48} viewBox="0 0 480 48" fill="none" style={{ position: 'absolute', inset: 0, filter: `drop-shadow(0 0 9px ${g.glow})` }}>
              <path d="M22 24 L40 4 L440 4 L458 24 L440 44 L40 44 Z" fill={g.fill} stroke={g.stroke} strokeWidth={1.4} />
              <path d="M28 24 L43 8 L437 8 L452 24 L437 40 L43 40 Z" fill="none" stroke={g.inner} strokeWidth={0.8} />
            </svg>
            <span style={{ position: 'relative', font: "700 17px/1 'Inter'", letterSpacing: 3, color: g.text, whiteSpace: 'nowrap' }}>GRADE {g.grade} &nbsp;&#8226;&nbsp; {g.label}</span>
          </div>
        </div>

        {/* BODY */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 34, marginTop: 22 }}>
          {/* LEFT COLUMN */}
          <div style={{ width: 566, flex: 'none', display: 'flex', flexDirection: 'column', gap: 22 }}>
            {/* DEVICE ATTESTATION */}
            <div style={{ position: 'relative', flex: 'none', background: panelBg, border: panelBorder, borderRadius: 10, boxShadow: panelShadow, padding: '16px 24px 8px' }}>
              <Corners />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
                <span style={sectionTitle}>DEVICE ATTESTATION</span>
                <span style={{ width: 6, height: 6, background: '#9c855a', transform: 'rotate(45deg)' }} />
                <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg,rgba(176,146,82,0.4),transparent)' }} />
              </div>
              {attRow('DEVICE', deviceLabel)}
              {attRow('SESSION', session, true)}
              {attRow('MERKLE ROOT', merkleRoot, true)}
              {attRow('ROOT SOURCE', rootSource)}
              {attRow('ATTESTATION KEY', attKey, true)}
              {attRow('ATTESTATION LEVEL', attLevel, false, true)}
            </div>

            {/* VERIFICATION */}
            <div style={{ position: 'relative', flex: 1, minHeight: 0, background: panelBg, border: panelBorder, borderRadius: 10, boxShadow: panelShadow, padding: '14px 24px 18px', display: 'flex', flexDirection: 'column' }}>
              <Corners />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
                <span style={{ width: 60, height: 1, background: 'linear-gradient(90deg,transparent,rgba(176,146,82,0.55))' }} />
                <span style={{ width: 5, height: 5, background: '#9c855a', transform: 'rotate(45deg)' }} />
                <span style={{ font: "600 16px/1 'Cinzel',serif", letterSpacing: 4, color: '#d8b56a', whiteSpace: 'nowrap' }}>VERIFICATION</span>
                <span style={{ width: 5, height: 5, background: '#9c855a', transform: 'rotate(45deg)' }} />
                <span style={{ width: 60, height: 1, background: 'linear-gradient(90deg,rgba(176,146,82,0.55),transparent)' }} />
              </div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 34, marginTop: 6 }}>
                {/* shield + medallion */}
                <div style={{ position: 'relative', width: 200, height: 200, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ position: 'absolute', inset: 0, opacity: 0.5 }}>
                    <Rosette stroke="rgba(216,184,114,0.85)" sw={0.5} dens={120} />
                  </div>
                  <svg width={138} height={160} viewBox="0 0 100 116" fill="none" style={{ position: 'relative', filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.55))' }}>
                    <defs>
                      <linearGradient id="shGold" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#f8e9bb" /><stop offset="0.45" stopColor="#d4a957" /><stop offset="1" stopColor="#9c7330" /></linearGradient>
                      <linearGradient id="shFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#27314a" /><stop offset="1" stopColor="#0c1424" /></linearGradient>
                    </defs>
                    <path d="M50 3 L92 17 V57 C92 86 73 103 50 112 C27 103 8 86 8 57 V17 Z" fill="url(#shFill)" stroke="url(#shGold)" strokeWidth={5} />
                    <path d="M50 9 L86 21 V57 C86 82 70 97 50 105 C30 97 14 82 14 57 V21 Z" fill="none" stroke="rgba(248,233,187,0.18)" strokeWidth={1} />
                    <path d="M31 57 L45 71 L71 39" stroke="url(#shGold)" strokeWidth={7} fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                {/* QR — real, scannable */}
                <div style={{ flex: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 13 }}>
                  <div style={{ width: 158, height: 158, background: '#f3f4f0', borderRadius: 10, padding: 13, boxShadow: '0 4px 14px rgba(0,0,0,0.4)' }}>
                    <QRCodeSVG value={verifyUrl} size={132} bgColor="#f3f4f0" fgColor="#0c1018" level="M" style={{ display: 'block', width: 132, height: 132 }} />
                  </div>
                  <span style={{ font: "600 14px/1 'Inter'", letterSpacing: 3, color: '#aeb8c6' }}>SCAN TO VERIFY</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, font: "400 13px/1 'JetBrains Mono'", color: '#8493a6', paddingTop: 6 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#9c855a" strokeWidth={1.6}><rect x={3} y={4} width={18} height={18} rx={2} /><path d="M3 9h18M8 2v4M16 2v4" /></svg>
                  Rekor {rekorIndex ? `#${rekorIndex}` : 'offline witness'}
                </span>
                <span style={{ color: '#6c5a30' }}>&#8226;</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <svg width={14} height={15} viewBox="0 0 24 26" fill="none" stroke="#9c855a" strokeWidth={1.6}><path d="M12 2 21 5v7c0 6-4 9-9 11C7 21 3 18 3 12V5Z" /><path d="m8.5 12.5 2.5 2.5 4.5-4.5" /></svg>
                  {otsCals > 0 ? `OTS · ${otsCals} cal (${dash(otsStatus)})` : 'OTS · awaiting calendar'}
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: 13 PILLAR VERDICTS */}
          <div style={{ position: 'relative', flex: 1, minWidth: 0, background: panelBg, border: panelBorder, borderRadius: 10, boxShadow: panelShadow, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Corners />
            <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '18px 22px 14px' }}>
              <span style={sectionTitle}>13-PILLAR VERDICTS &#183; MERKLE LEAVES</span>
              <span style={{ width: 6, height: 6, background: '#9c855a', transform: 'rotate(45deg)' }} />
              <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg,rgba(176,146,82,0.4),transparent)' }} />
            </div>
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '0 4px' }}>
              {pillars.map((p, i) => (
                <div key={p.pillar} style={{ display: 'grid', gridTemplateColumns: '42px 1fr 240px 128px', alignItems: 'center', columnGap: 14, height: 42, padding: '0 18px', borderBottom: i < pillars.length - 1 ? '1px solid rgba(176,146,82,0.1)' : 'none' }}>
                  <span style={{ font: "500 16px/1 'JetBrains Mono'", color: '#5e90d8' }}>{String(i + 1).padStart(2, '0')}</span>
                  <span style={{ font: "500 16px/1 'Inter'", color: '#e8edf5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</span>
                  <span style={{ font: "400 10.5px/1.32 'JetBrains Mono'", color: '#6f7d96', wordBreak: 'break-all' }}><span style={{ color: '#56627a' }}>leaf </span>{p.merkleLeaf || '—'}</span>
                  <VerdictPill verdict={p.verdict} />
                </div>
              ))}
              {pillars.length === 0 && (
                <div style={{ padding: '40px 18px', textAlign: 'center', font: "500 15px/1.5 'Inter'", color: '#56627a' }}>No terminal pillar verdicts recorded yet.</div>
              )}
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div style={{ position: 'relative', flex: 'none', marginTop: 18, background: panelBg, border: panelBorder, borderRadius: 10, boxShadow: 'inset 0 0 0 1px rgba(176,146,82,0.05)', padding: '14px 0 0' }}>
          <Corners />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0 26px' }}>
              <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="#c9a25a" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8z" /><path d="M14 3v5h5" /><path d="M8 13h8M8 17h5" /><circle cx={9} cy={10} r={1} /></svg>
              <div><div style={{ font: "600 11px/1 'Inter'", letterSpacing: 1.6, color: '#8493a6' }}>CERTIFICATE ID</div><div style={{ font: "500 16px/1 'JetBrains Mono'", color: '#dfe6ef', marginTop: 8 }}>{certId}</div></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0 26px', borderLeft: '1px solid rgba(176,146,82,0.16)' }}>
              <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="#c9a25a" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M16 3 21 8 8 21H3v-5z" /><path d="M14 5l5 5" /></svg>
              <div style={{ minWidth: 0 }}><div style={{ font: "600 11px/1 'Inter'", letterSpacing: 1.6, color: '#8493a6' }}>ISSUER SIGNATURE (P-384)</div><div style={{ font: "500 12px/1.5 'JetBrains Mono'", color: '#dfe6ef', marginTop: 8, wordBreak: 'break-all' }}>{issuerSig}</div></div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0 26px', borderLeft: '1px solid rgba(176,146,82,0.16)' }}>
              <svg width={26} height={26} viewBox="0 0 24 24" fill="none" stroke="#c9a25a" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><circle cx={12} cy={12} r={9} /><path d="M3 12h18M12 3c2.5 2.5 3.8 5.8 3.8 9s-1.3 6.5-3.8 9c-2.5-2.5-3.8-5.8-3.8-9S9.5 5.5 12 3z" /></svg>
              <div style={{ minWidth: 0 }}><div style={{ font: "600 11px/1 'Inter'", letterSpacing: 1.6, color: '#8493a6', wordBreak: 'break-all' }}>{issuerDid}</div><div style={{ font: "500 16px/1 'JetBrains Mono'", color: '#dfe6ef', marginTop: 8 }}>RETRONICS SIGNING AUTHORITY</div></div>
            </div>
          </div>
          <div style={{ marginTop: 14, borderTop: '1px solid rgba(176,146,82,0.14)', textAlign: 'center', padding: '10px 0', font: "500 11px/1 'JetBrains Mono'", letterSpacing: 1.4, color: '#5d6b82' }}>{cert.key_attested ? 'THIS CERTIFICATE IS HARDWARE-BOUND AND PUBLICLY VERIFIABLE.' : 'THIS CERTIFICATE IS SOFTWARE-SIGNED AND PUBLICLY VERIFIABLE.'} ANY ALTERATION WILL INVALIDATE THE SIGNATURE.</div>
        </div>
      </div>

      {/* RIGHT SPINE */}
      <div style={{ position: 'absolute', right: 21, top: 21, bottom: 21, width: 150, borderRadius: 3, overflow: 'hidden', borderLeft: '1px solid rgba(193,162,96,0.5)', borderRight: '1px solid rgba(193,162,96,0.5)', background: 'linear-gradient(180deg,#5a3a78 0%,#41538a 12%,#2f6f82 25%,#2c8270 37%,#2f74ac 47%,#3f8a64 59%,#7f8a44 71%,#9a6a40 82%,#7a3a64 93%,#4a2a5c 100%)' }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflow: 'hidden' }}><SpineCol /></div>
        <div style={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 60px rgba(0,0,0,0.35)' }} />
        <div style={{ position: 'absolute', left: '50%', top: 30, width: 1.5, height: 402, transform: 'translateX(-50%)', background: 'linear-gradient(180deg,transparent,rgba(248,233,187,0.85) 14%,rgba(248,233,187,0.85) 86%,transparent)' }} />
        <div style={{ position: 'absolute', left: '50%', top: 206, width: 9, height: 9, transform: 'translate(-50%,-50%) rotate(45deg)', background: '#f3e0b0', boxShadow: '0 0 8px rgba(248,233,187,0.8)' }} />
        {/* hex emblem */}
        <div style={{ position: 'absolute', left: '50%', top: 470, transform: 'translate(-50%,-50%)', width: 78, height: 86, clipPath: 'polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)', background: 'linear-gradient(180deg,#f8e9bb,#c79a4c)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 70, height: 78, clipPath: 'polygon(50% 0,100% 25%,100% 75%,50% 100%,0 75%,0 25%)', background: 'linear-gradient(180deg,#101d36,#0a1426)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ font: "700 34px/1 'Cinzel',serif", background: 'linear-gradient(180deg,#f8e9bb,#cb9b4d)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent', color: 'transparent' }}>R</span>
          </div>
        </div>
        {/* vertical wordmark */}
        <div style={{ position: 'absolute', left: '50%', bottom: 40, transform: 'translateX(-50%)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', font: "600 28px/1 'Cinzel',serif", letterSpacing: 9, color: '#f3e8cf', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>RETRONICS</span>
          <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', font: "600 11px/1 'Inter'", letterSpacing: 4, color: '#e6d6b2', paddingTop: 6 }}>SECURE &#183; VERIFIED &#183; TRUSTED</span>
        </div>
      </div>
    </div>
  );
}

// ── scale-to-fit wrapper ─────────────────────────────────────────────────────
export function CertificateDoc({ cert, sessionId }: { cert: CertDocData; sessionId?: string | undefined }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  // Canvas grows taller with pillar count so all verdicts + full Merkle leaves
  // fit without clipping — a full 13-pillar run needs more room than the base.
  const nPillars = cert.bundle?.credentialSubject?.pillars?.length ?? 0;
  const dh = Math.max(DH, 470 + 44 * nPillars);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      if (w > 0) setScale(Math.min(1, w / DW));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ width: '100%' }}>
      <div style={{ width: DW * scale, height: dh * scale, margin: '0 auto' }}>
        <div style={{ width: DW, height: dh, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          <Certificate1586 cert={cert} sessionId={sessionId} dh={dh} />
        </div>
      </div>
    </div>
  );
}
